import { useInfiniteQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";

// Mirrors apps/api/src/routes/activity.ts (house style: types duplicated
// client-side, cf. use-insights.ts).
export type ActivityType = "job" | "post" | "boost" | "reply" | "review";
export type ActivityActor = "ai" | "user" | "system";

export interface ActivityEvent {
  id: string;
  type: ActivityType;
  at: string; // ISO
  title: string;
  detail: string | null;
  href: string | null; // relative to app root, e.g. "/acme/pieces/P-1234"
  actor: ActivityActor;
  /** Structured details, present on `post` events. */
  post?: ActivityPostDetails;
}

export interface ActivityPostDetails {
  status: "PUBLISHED" | "FAILED" | "SCHEDULED" | "CANCELLED";
  platform: string;
  postType: string | null;
  groupId: string | null;
  scheduledFor: string;
  caption: string | null;
  mediaUrl: string | null;
  thumbnailUrl: string | null;
  error: string | null;
}

export interface ActivityPage {
  events: ActivityEvent[];
  nextCursor: string | null;
}

async function fetchPage(
  brandId: string,
  types: ActivityType[] | undefined,
  cursor?: string,
): Promise<ActivityPage> {
  const search = new URLSearchParams();
  if (types && types.length > 0) search.set("types", types.join(","));
  if (cursor) search.set("cursor", cursor);
  const qs = search.toString();
  return apiFetch<ActivityPage>(`/brands/${brandId}/activity${qs ? `?${qs}` : ""}`);
}

export function useActivity(brandId: string | undefined, types?: ActivityType[]) {
  return useInfiniteQuery({
    queryKey: ["activity", brandId, types?.join(",") ?? "all"],
    queryFn: ({ pageParam }) => fetchPage(brandId!, types, pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!brandId,
    staleTime: 15_000,
  });
}
